import React, { memo, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { MediaClientConfig } from '@atlaskit/media-core';
import { Browser } from '@atlaskit/media-picker';
import type { UploadRejectionData } from '@atlaskit/media-picker';
import type { UploadEndEventPayload } from '@atlaskit/media-picker/types';
import { token } from '@atlaskit/tokens';

import { markErrorAsHandled } from '@confluence/graphql';
import { getMonitoringClient } from '@confluence/monitoring';
import {
	COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
	COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
	COVER_PICTURE_REMOVE_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import {
	createEditorMediaProvider,
	getAttachmentSizeLimitErrorFlag,
	createRendererMediaProvider,
} from '@confluence/fabric-media-support';
import { ExternalShareContext } from '@confluence/external-share-context';
import type { WithFlagsProps } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import { usePubSubEvent } from '@confluence/pubsub-client';
import {
	Attribution,
	withTransparentErrorBoundary,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager/entry-points/getDraftShareIdFromQueryParam';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';
import type { COVERPICTUREWIDTH } from '@confluence/custom-sites-extensions';

import { propertyToId } from '../coverPictureHelperFunctions';
import type {
	CoverPictureId,
	EditorPageCoverPictureComponentProps,
} from '../pageCoverPictureTypes';
import { CoverPictureIdPropertyKey } from '../pageCoverPictureTypes';
import { DEFAULT_POSITION_VALUE } from '../pageCoverPictureConstants';
import { UpdateCoverPictureIdMutation } from '../UpdateCoverPictureIdMutation.experimentalgraphql.js';
import type { UpdateCoverPictureIdMutation as UpdateCoverPictureIdMutationType } from '../__types__/UpdateCoverPictureIdMutation';
import { CoverPictureQuery } from '../CoverPictureQuery.graphql';
import type {
	CoverPictureQuery as CoverPictureQueryType,
	CoverPictureQueryVariables,
} from '../__types__/CoverPictureQuery';

import { UpdateCoverPictureWidth } from './UpdateCoverPictureWidth.graphql';
import { MediaCoverPicture } from './MediaCoverPicture';
import { ImagePicker } from './ImagePicker';
import { UnsplashCoverPicture } from './UnsplashCoverPicture';
import { useBooleanFeatureFlag } from '@confluence/session-data';

const i18n = defineMessages({
	errorFlagTitle: {
		id: 'page-cover-picture.error.flag.title',
		description: 'Title within the error flag that appears if some upload image error occurred.',
		defaultMessage: 'We can’t upload your header image',
	},
	errorFlagDescription: {
		id: 'page-cover-picture.error.flag.description',
		description:
			'Description within the error flag that appears if some upload image error occurred.',
		defaultMessage: 'Refresh the page and try again.',
	},
});

type CoverPictureProps = {
	hasCoverPicture: boolean;
	isUploadingPicture: boolean;
	userOffline: boolean;
	isTitleHovered?: boolean;
	width?: number;
	isCustomSitesPageTitleFFOn?: boolean;
	isFixedWidthToggleFFOn?: boolean;
	dynamicCoverImageHeight?: number;
	isReadOnly?: boolean;
};

// if page has cover picture or is uploading picture, make space for the photo
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CoverPictureWrapper = styled.div<CoverPictureProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({
		isCustomSitesPageTitleFFOn,
		hasCoverPicture,
		isUploadingPicture,
		dynamicCoverImageHeight,
	}) =>
		hasCoverPicture || isUploadingPicture
			? isCustomSitesPageTitleFFOn
				? `${dynamicCoverImageHeight}px`
				: '140px'
			: '0px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ isCustomSitesPageTitleFFOn, width, hasCoverPicture, isUploadingPicture }) =>
		(hasCoverPicture || isUploadingPicture) && isCustomSitesPageTitleFFOn && width
			? `${width}px`
			: '100%',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isReadOnly }) => (!isReadOnly ? 'pointer' : 'auto'),
	marginBottom: token('space.100', '8px'),
	/* eslint-disable @atlaskit/design-system/ensure-design-token-usage/preview */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ hasCoverPicture, isUploadingPicture }) =>
		hasCoverPicture || isUploadingPicture ? token('space.075', '6px') : token('space.0', '0px'),
	/* eslint-enable @atlaskit/design-system/ensure-design-token-usage/preview */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderRadius: ({ isCustomSitesPageTitleFFOn }) => (isCustomSitesPageTitleFFOn ? 'unset' : '3px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ImageWrapper = styled.img<{
	isCustomSitesPageTitleFFOn?: boolean;
	coverPicturePosition?: number | null;
	dynamicCoverImageHeight?: number;
}>({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ isCustomSitesPageTitleFFOn, dynamicCoverImageHeight }) =>
		isCustomSitesPageTitleFFOn ? `${dynamicCoverImageHeight}px` : '140px',
	objectFit: 'cover',
	position: 'absolute',
	'&:hover': {
		opacity: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderRadius: ({ isCustomSitesPageTitleFFOn }) => (isCustomSitesPageTitleFFOn ? 'unset' : '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	objectPosition: ({ coverPicturePosition }) => `center ${coverPicturePosition}%`,
});

const EditorPageCoverPictureComponent = memo(
	({
		contentId,
		isTitleHovered,
		isPublishingContent,
		contentType,
		userOffline,
		setIsModalOpen,
		isModalOpen,
		hasCoverPicture,
		coverPictureState,
		isUnpublishedDraft,
		flags,
		floatingToolbarButtonStyle,
		isCustomSitesPageTitleFFOn,
		isFixedWidthToggleFFOn,
		width,
		dynamicCoverImageHeight,
		isReadOnly,
		spaceKey,
	}: EditorPageCoverPictureComponentProps & WithFlagsProps) => {
		const [isMediaBrowserOpen, setIsMediaBrowserOpen] = useState<boolean>(false);
		const { isExternalShareRequest: isExternalShare } = useContext(ExternalShareContext);
		const [isUploadingPicture, setIsUploadingPicture] = useState<boolean>(false);
		const isEditorSSREnabled = useBooleanFeatureFlag('confluence.frontend.editor.ssr');
		// mediaConfig does not use onUploadRejection when in read only (RendererMediaProvider), but does need it otherwise (EditorMediaProvider).
		const [mediaConfig, setMediaConfig] = useState<{
			collection: string | undefined;
			authProvider: MediaClientConfig['authProvider'];
			onUploadRejection?: (data: UploadRejectionData) => boolean;
			useSha256ForUploads?: boolean;
		} | null>(null);
		// Using ref since onHandleUploadEnd event uses previous cover picture information and upload will fail
		// since the information is from the past render
		const coverPicture = useRef<{
			draft: CoverPictureId | null;
		}>({ draft: null });
		// need this force update since we are using useRef for the cover picture and therefore won't update the image automatically on change
		const [, forceUpdate] = useState<any>();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const isLivePage = useIsLivePage();
		// need this state since we want to show reposition option right after we upload a photo
		const [shouldShowReposition, setShouldShowReposition] = useState<boolean>(false);
		const experienceTracker = useContext(ExperienceTrackerContext);
		const { formatMessage } = useIntl();
		const intl = useIntl();

		const { data } = useQuery<CoverPictureQueryType, CoverPictureQueryVariables>(
			CoverPictureQuery,
			{
				context: { single: true },
				variables: {
					contentId,
					status: ['current', 'draft'],
					draftShareId: getDraftShareIdFromQueryParam(),
				},
			},
		);
		const [mutateCoverPictureId] = useMutation<UpdateCoverPictureIdMutationType>(
			UpdateCoverPictureIdMutation,
			{
				refetchQueries: () => [
					{
						query: CoverPictureQuery,
						variables: {
							contentId,
							status: ['draft'],
							draftShareId: getDraftShareIdFromQueryParam(),
						},
					},
				],
			},
		);
		const [mutateCoverPictureWidth] = useMutation<any>(UpdateCoverPictureWidth, {
			onError: (error) => {
				if (isUnauthorizedError(error)) {
					markErrorAsHandled(error);
				}
				getMonitoringClient().submitError(error, {
					attribution: Attribution.COMPANY_HUB,
				});
			},
			ignoreResults: true, // don't want to update cache with content info
		});

		// update draft's cover id once we get the new info back from pubsub
		const onCoverPictureUpdated = useCallback(
			(_eventName: any, pubsubData: any) => {
				const value = JSON.parse(pubsubData.contentPropertyUpdateRequestBody.value);
				const version = pubsubData.contentPropertyUpdateRequestBody.version;
				void coverPictureState.setHasCoverPicture(true);
				coverPicture.current['draft'] = { value, version };
				setIsUploadingPicture(false);
				void coverPictureState.setIsUploadingPicture(false);
				forceUpdate(Math.random());
			},
			[coverPictureState],
		);

		const onCoverPictureWidthUpdated = useCallback(
			(_eventName: any, pubsubData: any) => {
				const value = pubsubData.contentCoverPictureWidthRequestBody.coverPictureWidth;
				void coverPictureState.toggleCoverPictureWidthButton(value);
				forceUpdate(Math.random());
			},
			[coverPictureState],
		);

		const onCoverPictureDeleted = useCallback(
			(_eventName: any, _data: any) => {
				coverPicture.current['draft'] = null;
				void coverPictureState.setHasCoverPicture(false);
				forceUpdate(Math.random());
			},
			[coverPictureState],
		);

		const pubSubResourceType = contentType || 'page';
		// pub sub setup and cleanup
		usePubSubEvent({
			contentId,
			contentType: pubSubResourceType,
			eventName: 'updated:coverPicture',
			onEvent: onCoverPictureUpdated,
		});

		usePubSubEvent({
			contentId,
			contentType: pubSubResourceType,
			eventName: 'deleted:coverPicture',
			onEvent: onCoverPictureDeleted,
		});

		usePubSubEvent({
			contentId,
			contentType: pubSubResourceType,
			eventName: 'updated:coverPictureWidth',
			onEvent: onCoverPictureWidthUpdated,
		});

		// Check for existing cover picture
		useEffect(() => {
			const properties = data?.content?.nodes?.[0]?.properties?.nodes || [];
			const draft = properties.find((p) => p?.key === CoverPictureIdPropertyKey.DRAFT);
			coverPicture.current = {
				draft: propertyToId(draft),
			};
			forceUpdate(Math.random());
		}, [data]);

		// Setup media config
		useEffect(() => {
			const showAttachmentLimitReachedFlag = (rejectionData: UploadRejectionData) => {
				if (flags && rejectionData) {
					void flags
						.showErrorFlag(getAttachmentSizeLimitErrorFlag(rejectionData, intl))
						.then(() => {
							setIsUploadingPicture(false);
							void coverPictureState.setIsUploadingPicture(false);

							experienceTracker.abort({
								name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
								reason: 'Cover picture media upload too large',
							});

							createAnalyticsEvent({
								type: 'sendScreenEvent',
								data: {
									name: 'pageCoverPictureAttachmentTooLargeFlag',
								},
							}).fire();
						});
				}
				return !!rejectionData;
			};

			// If read only, use renderer media provider, as we only need the auth provider from rendererMediaProvider, and user without edit perms (read only) doesn't have access to editorMediaProvider APIs
			// If not read only, use editor media provider
			const createMediaConfig = async () => {
				// Media provider is not supported in SSR, skip it for now.
				if (process.env.REACT_SSR && isEditorSSREnabled) {
					return;
				}
				if (isReadOnly) {
					const { viewMediaClientConfig } = await createRendererMediaProvider({
						contentId,
						isExternalShare: false,
					});
					if (!viewMediaClientConfig) {
						return null;
					}
					setMediaConfig({
						collection: `contentId-${contentId}`,
						authProvider: viewMediaClientConfig.authProvider,
					});
				} else {
					const { uploadParams, uploadMediaClientConfig } = await createEditorMediaProvider({
						contentId,
						contentType: 'page',
						isExternalShare,
						onUploadRejection: flags ? showAttachmentLimitReachedFlag : undefined,
					});

					if (uploadMediaClientConfig == undefined) {
						return null;
					}
					setMediaConfig({
						collection: uploadParams?.collection,
						authProvider: uploadMediaClientConfig.authProvider,
						onUploadRejection: uploadParams?.onUploadRejection,
						useSha256ForUploads: uploadMediaClientConfig?.useSha256ForUploads,
					});
				}
			};

			void createMediaConfig();
		}, [
			contentId,
			isExternalShare,
			flags,
			intl,
			coverPictureState,
			createAnalyticsEvent,
			experienceTracker,
			isReadOnly,
			isEditorSSREnabled,
		]);

		// handling user publishes - we only need to worry about firing the analytic event as backend will take care of the
		// published cover picture content property mutation due to scheduled publishes
		useEffect(() => {
			// fire analytic if we're publishing a page with a cover image
			if (isPublishingContent && !!coverPicture.current?.draft?.value.id) {
				if (isUnpublishedDraft) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'createdWithHeaderImage',
							actionSubject: 'page',
							objectId: contentId,
							source: 'editorPageCoverPicture',
						},
					}).fire();
				} else {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'updatedWithHeaderImage',
							actionSubject: 'page',
							objectId: contentId,
							source: 'editorPageCoverPicture',
						},
					}).fire();
				}
			}
		}, [contentId, isPublishingContent, isUnpublishedDraft, createAnalyticsEvent]);

		useEffect(() => {
			if (isPublishingContent && hasCoverPicture && isCustomSitesPageTitleFFOn) {
				void coverPictureState.setShouldShowReposition(false);
			}
		}, [hasCoverPicture, isPublishingContent, coverPictureState, isCustomSitesPageTitleFFOn]);

		const showErrorFlag = () => {
			void flags.showFlag({
				type: 'error',
				title: formatMessage(i18n.errorFlagTitle),
				description: formatMessage(i18n.errorFlagDescription),
			});
			setIsUploadingPicture(false);
			void coverPictureState.setIsUploadingPicture(false);

			experienceTracker.stopOnError({
				name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
				error: new Error('Error with cover picture media upload'),
			});

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'pageCoverPictureErrorFlag',
				},
			}).fire();
		};

		const handleUploadStart = () => {
			setIsUploadingPicture(true);
			void coverPictureState.setIsUploadingPicture(true);
			if (isCustomSitesPageTitleFFOn) {
				void coverPictureState.setIsChangingPicture(false);
			}
			experienceTracker.start({
				name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
			});
		};

		const handleUploadEnd = (event: UploadEndEventPayload) => {
			const fileType = event.file.type;
			const id = event?.file?.id;
			if (!fileType.includes('image')) {
				showErrorFlag();
			} else if (id) {
				// must JSON stringify propertyValue object since mutation expects a string for this parameter
				const propertyValue = JSON.stringify({
					id,
					position: DEFAULT_POSITION_VALUE,
				});
				const version = String((coverPicture.current?.draft?.version || 0) + 1);
				void mutateCoverPictureId({
					variables: {
						contentId,
						propertyValue,
						propertyKey: CoverPictureIdPropertyKey.DRAFT,
						versionNumber: version,
					},
				});
				setShouldShowReposition(true);
				if (isCustomSitesPageTitleFFOn) {
					void coverPictureState.setShouldShowReposition(true);
				}
				experienceTracker.succeed({
					name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'added',
						actionSubject: 'image',
						actionSubjectId: 'headerImageAdded',
						source: 'editorPageCoverPicture',
						attributes: {
							imageSource: 'uploaded',
							imageId: id,
							inputMethod: 'header',
							isLivePage,
						},
					},
				}).fire();
			} else {
				showErrorFlag();
			}
		};

		const handleUnsplashUpload = (photoURL) => {
			setIsUploadingPicture(true);
			void coverPictureState.setIsUploadingPicture(true);
			const propertyValue = JSON.stringify({
				id: photoURL,
				position: DEFAULT_POSITION_VALUE,
			});
			const version = String((coverPicture.current?.draft?.version || 0) + 1);
			void mutateCoverPictureId({
				variables: {
					contentId,
					propertyValue,
					propertyKey: CoverPictureIdPropertyKey.DRAFT,
					versionNumber: version,
				},
			});
			setShouldShowReposition(true);
			if (isCustomSitesPageTitleFFOn) {
				void coverPictureState.setShouldShowReposition(true);
			}
			experienceTracker.succeed({
				name: COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
			});
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'added',
					actionSubject: 'image',
					actionSubjectId: 'headerImageAdded',
					source: 'editorPageCoverPicture',
					attributes: {
						imageSource: 'unsplash',
						imageId: photoURL,
						inputMethod: 'header',
						isLivePage,
					},
				},
			}).fire();
		};

		const onRemoveImage = useCallback(async () => {
			void coverPictureState.setIsRemovingPicture(true);
			await mutateCoverPictureId({
				variables: {
					contentId,
					propertyKey: CoverPictureIdPropertyKey.DRAFT,
					isDelete: true,
				},
			});
			if (isCustomSitesPageTitleFFOn) {
				void coverPictureState.setIsChangingPicture(false);
			}
			void coverPictureState.setIsRemovingPicture(false);
			experienceTracker.succeed({
				name: COVER_PICTURE_REMOVE_EXPERIENCE,
			});
		}, [
			contentId,
			mutateCoverPictureId,
			coverPictureState,
			experienceTracker,
			isCustomSitesPageTitleFFOn,
		]);

		const onImageWidthChange = useCallback(
			async (coverPictureWidth: COVERPICTUREWIDTH) => {
				if (
					isCustomSitesPageTitleFFOn &&
					isFixedWidthToggleFFOn &&
					coverPictureWidth !== coverPictureState.state.coverPictureWidth
				) {
					void coverPictureState.toggleCoverPictureWidthButton(coverPictureWidth);

					//hack the input and outout have different capitalizations TT_TT
					// the mutation returns lowercase, the mutation expects uppercase
					await mutateCoverPictureWidth({
						variables: {
							contentId,
							coverPictureWidth: coverPictureWidth.toUpperCase(),
						},
					});
				}
			},
			[
				isCustomSitesPageTitleFFOn,
				isFixedWidthToggleFFOn,
				contentId,
				mutateCoverPictureWidth,
				coverPictureState,
			],
		);

		const onSaveReposition = async (position) => {
			// check that the position isn't the same as the current one
			if (position != coverPicture.current?.draft?.value.position) {
				const version = String((coverPicture.current?.draft?.version || 0) + 1);
				const propertyValue = JSON.stringify({
					id: coverPicture.current?.draft?.value.id,
					position,
				});
				coverPicture.current.draft!.value.position = position;
				coverPicture.current.draft!.version = parseInt(version);
				await mutateCoverPictureId({
					variables: {
						contentId,
						propertyValue,
						propertyKey: CoverPictureIdPropertyKey.DRAFT,
						versionNumber: version,
					},
				});
			}
		};

		const openMediaBrowser = () => {
			setIsMediaBrowserOpen(true);
		};

		const closeMediaBrowser = () => {
			setIsMediaBrowserOpen(false);
		};

		if (!mediaConfig) {
			return null;
		}

		const isUnsplash = coverPicture.current?.draft?.value.id.includes('images.unsplash.com');

		return (
			<div>
				<CoverPictureWrapper
					hasCoverPicture={hasCoverPicture}
					isUploadingPicture={isUploadingPicture}
					data-testid="cover-picture-wrapper"
					userOffline={userOffline}
					width={width}
					isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					isReadOnly={isReadOnly}
				>
					{hasCoverPicture &&
						mediaConfig &&
						(isUnsplash ? (
							<UnsplashCoverPicture
								coverPicture={coverPicture}
								isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
								dynamicCoverImageHeight={dynamicCoverImageHeight}
							/>
						) : (
							<MediaCoverPicture
								coverPicture={coverPicture}
								mediaConfig={mediaConfig}
								showErrorFlag={showErrorFlag}
								isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
								dynamicCoverImageHeight={dynamicCoverImageHeight}
							/>
						))}
					{!isReadOnly && (
						<ImagePicker
							hasCoverPicture={hasCoverPicture}
							isTitleHovered={isTitleHovered}
							openMediaBrowser={openMediaBrowser}
							onRemoveImage={onRemoveImage}
							userOffline={userOffline}
							isUploadingPicture={isUploadingPicture}
							handleUnsplashUpload={handleUnsplashUpload}
							setIsUploadingPicture={setIsUploadingPicture}
							setIsModalOpen={setIsModalOpen}
							isModalOpen={isModalOpen}
							initialPicturePosition={coverPicture.current.draft?.value.position}
							onSaveReposition={onSaveReposition}
							shouldShowReposition={shouldShowReposition}
							setShouldShowReposition={setShouldShowReposition}
							coverPictureState={coverPictureState}
							floatingToolbarButtonStyle={floatingToolbarButtonStyle}
							isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
							isFixedWidthToggleFFOn={isFixedWidthToggleFFOn}
							forceUpdate={forceUpdate}
							contentId={contentId}
							isExternalShare={isExternalShare}
							dynamicCoverImageHeight={dynamicCoverImageHeight}
							onImageWidthChange={onImageWidthChange}
							spaceKey={spaceKey}
						/>
					)}
				</CoverPictureWrapper>
				{!isReadOnly && (
					<Browser
						mediaClientConfig={{
							authProvider: mediaConfig?.authProvider,
							useSha256ForUploads: mediaConfig?.useSha256ForUploads,
						}}
						config={{
							multiple: false,
							fileExtensions: ['.jpg', '.jpeg', '.png', '.gif'],
							uploadParams: {
								collection: mediaConfig.collection,
								onUploadRejection: mediaConfig?.onUploadRejection,
							},
						}}
						isOpen={isMediaBrowserOpen}
						onClose={closeMediaBrowser}
						onUploadsStart={handleUploadStart}
						onError={showErrorFlag}
						onEnd={handleUploadEnd}
					/>
				)}
			</div>
		);
	},
);

export const EditorPageCoverPicture = withFlags(
	withTransparentErrorBoundary({
		attribution: Attribution.PAGE_EXTENSIONS,
	})(EditorPageCoverPictureComponent),
);
